import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AdminAuthService, AdminLoginRequest, AdminLoginResponse, AdminUser } from '@/modules/auth/services/auth.service';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface AuthState {
  user: AdminUser | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: AdminLoginResponse }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: AdminUser };

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken || null,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGOUT':
      return { ...initialState, isLoading: false };
    case 'UPDATE_USER':
      return { ...state, user: action.payload };
    default:
      return state;
  }
};

interface AuthContextType {
  state: AuthState;
  login: (credentials: AdminLoginRequest) => Promise<AdminLoginResponse>;
  logout: () => void;
  updateUser: (user: AdminUser) => void;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const queryClient = useQueryClient();

  useEffect(() => {
    const abortController = new AbortController();

    const checkAuthStatus = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        const token = localStorage.getItem('admin_token');
        const refreshToken = localStorage.getItem('admin_refresh_token');

        if (!token) {
          if (!abortController.signal.aborted) {
            dispatch({ type: 'SET_LOADING', payload: false });
          }
          return;
        }

        try {
          const user = await AdminAuthService.getCurrentUser(abortController.signal);

          if (abortController.signal.aborted) return;

          if (!user) throw new Error('No user data returned');

          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user,
              token,
              refreshToken: refreshToken || undefined,
              expiresAt: new Date(),
              appName: '',
            },
          });
        } catch {
          if (abortController.signal.aborted) return;

          if (refreshToken) {
            try {
              const response = await AdminAuthService.refreshToken(refreshToken, abortController.signal);

              if (abortController.signal.aborted) return;

              localStorage.setItem('admin_token', response.token);
              if (response.refreshToken) {
                localStorage.setItem('admin_refresh_token', response.refreshToken);
              }

              dispatch({ type: 'LOGIN_SUCCESS', payload: response });
            } catch {
              if (!abortController.signal.aborted) {
                AdminAuthService.logout();
                dispatch({ type: 'LOGOUT' });
              }
            }
          } else {
            if (!abortController.signal.aborted) {
              AdminAuthService.logout();
              dispatch({ type: 'LOGOUT' });
            }
          }
        }
      } catch {
        if (!abortController.signal.aborted) {
          AdminAuthService.logout();
          dispatch({ type: 'LOGOUT' });
        }
      }
    };

    checkAuthStatus();

    return () => {
      abortController.abort();
    };
  }, []);

  const login = async (credentials: AdminLoginRequest): Promise<AdminLoginResponse> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      queryClient.clear();

      const response = await AdminAuthService.login(credentials);

      localStorage.setItem('admin_token', response.token);
      if (response.refreshToken) {
        localStorage.setItem('admin_refresh_token', response.refreshToken);
      }

      dispatch({ type: 'LOGIN_SUCCESS', payload: response });
      return response;
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const logout = () => {
    queryClient.clear();
    AdminAuthService.logout();
    dispatch({ type: 'LOGOUT' });
    toast.success('Logout realizado com sucesso!');
  };

  const updateUser = (user: AdminUser) => {
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  const refreshAuth = async () => {
    const refreshToken = localStorage.getItem('admin_refresh_token');

    if (!refreshToken) throw new Error('No refresh token available');

    try {
      const response = await AdminAuthService.refreshToken(refreshToken);
      localStorage.setItem('admin_token', response.token);
      if (response.refreshToken) {
        localStorage.setItem('admin_refresh_token', response.refreshToken);
      }
      dispatch({ type: 'LOGIN_SUCCESS', payload: response });
    } catch (error) {
      logout();
      throw error;
    }
  };

  const value: AuthContextType = {
    state,
    login,
    logout,
    updateUser,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
